import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PageHeader from '../components/ui/PageHeader';
import MetricCard from '../components/ui/MetricCard';
import SmartPayIcon from '../components/ui/SmartPayIcon';
import { getAffiliations, getMerchants, getTerminals } from '../services/adminService';

const Overview = () => {
    const [data, setData] = useState({ affiliations: [], merchants: [], terminals: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const [affiliations, merchants, terminals] = await Promise.all([getAffiliations(), getMerchants(), getTerminals()]);
            setData({ affiliations, merchants, terminals });
        } catch (requestError) {
            setError('No fue posible cargar el resumen operativo.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const terminalStatus = useMemo(() => {
        const total = data.terminals.length || 1;
        const active = data.terminals.filter((item) => item.status === 'active').length;
        const pending = data.terminals.filter((item) => item.status === 'pending').length;
        const blocked = data.terminals.filter((item) => item.status === 'blocked').length;
        return { total: data.terminals.length, active, pending, blocked, activePercent: Math.round(active * 100 / total) };
    }, [data.terminals]);

    if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 420 }}><CircularProgress /></Box>;

    return (
        <Stack spacing={3.5}>
            <PageHeader eyebrow="Centro de operación" title="Todo SmartPay, en un solo lugar" description="Supervisa tu red, configura el producto y mantén el control operativo con información clara y accionable." />
            {error && <Alert severity="warning">{error}</Alert>}

            <Grid container spacing={2.2}>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Afiliaciones" value={data.affiliations.length} helper="Estructuras comerciales" icon={<SmartPayIcon name="affiliations" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Comercios" value={data.merchants.length} helper="Puntos comerciales" icon={<SmartPayIcon name="merchants" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Terminales" value={terminalStatus.total} helper="Dispositivos registrados" icon={<SmartPayIcon name="terminals" size={62} />} /></Grid>
                <Grid item xs={12} sm={6} xl={3}><MetricCard label="Disponibilidad" value={`${terminalStatus.activePercent}%`} helper={`${terminalStatus.active} terminales activas`} icon={<SmartPayIcon name="availability" size={62} />} /></Grid>
            </Grid>

            <Grid container spacing={2.5}>
                <Grid item xs={12} lg={7}>
                    <Card sx={{ height: '100%' }}><CardContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                            <Box><Typography variant="h6">Estado de la red</Typography><Typography variant="body2" color="text.secondary">Distribución actual de terminales</Typography></Box>
                            <Button component={RouterLink} to="../terminals" endIcon={<ArrowForwardRoundedIcon />}>Ver terminales</Button>
                        </Stack>
                        {[
                            { label: 'Activas', value: terminalStatus.active, color: 'success' },
                            { label: 'Pendientes', value: terminalStatus.pending, color: 'warning' },
                            { label: 'Bloqueadas', value: terminalStatus.blocked, color: 'error' },
                        ].map((item) => {
                            const percent = terminalStatus.total ? item.value * 100 / terminalStatus.total : 0;
                            return <Box key={item.label} sx={{ mb: 2.5 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: .8 }}><Typography variant="body2" sx={{ fontWeight: 650 }}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.value}</Typography></Stack>
                                <LinearProgress variant="determinate" value={percent} color={item.color} sx={{ height: 9, borderRadius: 6, bgcolor: 'action.hover' }} />
                            </Box>;
                        })}
                    </CardContent></Card>
                </Grid>
                <Grid item xs={12} lg={5}>
                    <Card sx={{ height: '100%', color: '#FFF', overflow: 'hidden', background: 'linear-gradient(145deg, #172D43 0%, #3A3278 62%, #007F77 150%)' }}>
                        <CardContent sx={{ p: 3.5, position: 'relative', height: '100%' }}>
                            <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(82,228,214,.10)', right: -70, top: -80 }} />
                            <Typography variant="overline" sx={{ color: '#72E6DC', fontWeight: 800, letterSpacing: '.14em' }}>SMARTPAY CONTROL</Typography>
                            <Typography variant="h5" sx={{ mt: 1, maxWidth: 360 }}>Configura una vez. Opera con confianza.</Typography>
                            <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,.70)', maxWidth: 430 }}>Centraliza modelos, operativas y reglas para que cada terminal reciba exactamente lo que necesita.</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 4 }}>
                                <Button component={RouterLink} to="../terminal-models" variant="contained" color="secondary">Modelos</Button>
                                <Button component={RouterLink} to="../operational-configuration" variant="outlined" sx={{ color: '#FFF', borderColor: 'rgba(255,255,255,.35)' }}>Operativas</Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Stack>
    );
};

export default Overview;
